import React, { useState, useRef } from 'react';
import PdfUploadEditor from './PdfUploadEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

export default function PDFReviewEmbed({
  pdfFilename,
  documentName,
  saleData,
  signerName,
  signerEmail,
  stockNumber,
  onSuccess,
  onCancel,
}) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [pdfOpened, setPdfOpened] = useState(false);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [showPdfEditor, setShowPdfEditor] = useState(false);

  const getToken = () =>
    localStorage.getItem('auth_token') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') || '';

  const pdfUrl = `${API_BASE}/api/temp-pdf/${encodeURIComponent(pdfFilename)}?token=${encodeURIComponent(getToken())}`;

  const handleViewPDF = () => {
    window.open(pdfUrl, '_blank');
    setPdfOpened(true);
  };

  const handlePdfEditorComplete = (data) => {
    setAdditionalDocs(prev => [...prev, data]);
    setShowPdfEditor(false);
  };

  const removeDoc = (index) => {
    setAdditionalDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleApprove = async () => {
    setSending(true);
    setError(null);
    setStatus('Sending to Documenso...');

    try {
      const token = getToken();
      // Extract filename from pdfUrl instead of re-sending entire base64
      const pdfFilename = pdfUrl.split('/').pop().split('?')[0];

      const formData = new FormData();
      formData.append('pdfFilename', pdfFilename);
      formData.append('saleData', JSON.stringify(saleData));
      formData.append('documentName', documentName);
      formData.append('signerName', signerName);
      formData.append('signerEmail', signerEmail);
      formData.append('stockNumber', stockNumber);
      formData.append('sendMode', saveAsDraft ? 'draft' : 'send');
      additionalDocs.forEach(d => {
        formData.append('additionalDocs', d.file);
        formData.append('additionalDocsFields', JSON.stringify(d.fields));
      });

      const response = await fetch(`${API_BASE}/api/documents/approve`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Send failed');
      setStatus('✅ Sent successfully!');
      onSuccess && onSuccess(result);

    } catch (err) {
      setError(err.message);
      setStatus('');
      setSending(false);
    }
  };

  // ── PDF Editor for additional docs ─────────────────────────────────────────
  if (showPdfEditor) {
    return (
      <div style={{ minHeight: '100vh', background: '#1a1a1a', fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: '#111', borderBottom: '2px solid #e63329' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>📎 Add Additional Document</div>
          <button
            onClick={() => setShowPdfEditor(false)}
            style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', fontSize: 14 }}
          >
            ← Back to Review
          </button>
        </div>
        <div style={{ padding: '24px' }}>
          <PdfUploadEditor
            onComplete={handlePdfEditorComplete}
            onCancel={() => setShowPdfEditor(false)}
          />
        </div>
      </div>
    );
  }

  // ── Main review screen ─────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#1a1a1a', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: '#111', borderBottom: '2px solid #e63329', flexShrink: 0 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>📄 Review Document</div>
          <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
            {documentName} &nbsp;·&nbsp; {signerName} &lt;{signerEmail}&gt;
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {status && <span style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>{status}</span>}
          <button onClick={onCancel} disabled={sending} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', fontSize: 14 }}>
            ← Back
          </button>
          <button onClick={handleApprove} disabled={sending} style={{ padding: '10px 28px', borderRadius: 6, border: 'none', background: sending ? '#555' : '#e63329', color: '#fff', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, boxShadow: sending ? 'none' : '0 2px 8px rgba(230,51,41,0.4)' }}>
            {sending ? '⏳ Sending...' : '✓ Approve & Send to Documenso'}
          </button>
        </div>
      </div>

      {/* Error bar */}
      {error && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 24px', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ background: '#111', border: '1px solid #333', borderRadius: 12, padding: '40px 48px', maxWidth: 540, width: '100%' }}>

          <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 16 }}>📄</div>
          <h2 style={{ color: '#fff', margin: '0 0 6px', fontSize: 20, textAlign: 'center' }}>{documentName}</h2>
          <p style={{ color: '#888', margin: '0 0 28px', fontSize: 14, textAlign: 'center' }}>{signerName} &nbsp;·&nbsp; Stock #{stockNumber}</p>

          {/* View PDF */}
          <button
            onClick={handleViewPDF}
            style={{ display: 'block', width: '100%', padding: '13px', borderRadius: 8, border: '2px solid #444', background: 'transparent', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 6 }}
            onMouseOver={e => e.currentTarget.style.borderColor = '#888'}
            onMouseOut={e => e.currentTarget.style.borderColor = '#444'}
          >
            👁 Open PDF for Review
          </button>
          <p style={{ color: '#555', fontSize: 12, margin: '0 0 24px', textAlign: 'center' }}>
            Opens in a new tab — view in browser or download to open in Acrobat
          </p>

          <div style={{ borderTop: '1px solid #2a2a2a', margin: '0 0 24px' }} />

          {/* Additional Documents */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
              📎 Additional Documents <span style={{ color: '#555', fontWeight: 400 }}>(Optional)</span>
            </div>

            {additionalDocs.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {additionalDocs.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e1e1e', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                    <span style={{ color: '#ccc', fontSize: 13 }}>📄 {d.file.name} <span style={{ color: '#666' }}>— {d.fields.length} field(s)</span></span>
                    <button onClick={() => removeDoc(i)} style={{ background: 'none', border: 'none', color: '#e63329', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowPdfEditor(true)}
              style={{ display: 'block', width: '100%', padding: '10px', borderRadius: 8, border: '1px dashed #444', background: 'transparent', color: '#888', fontSize: 13, cursor: 'pointer' }}
              onMouseOver={e => e.currentTarget.style.borderColor = '#888'}
              onMouseOut={e => e.currentTarget.style.borderColor = '#444'}
            >
              + Add PDF & Place Signature Fields
            </button>
          </div>

          {/* Draft toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 24, padding: '12px', background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8 }}>
            <input type="checkbox" checked={saveAsDraft} onChange={e => setSaveAsDraft(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <div>
              <div style={{ color: '#ccc', fontSize: 14, fontWeight: 600 }}>Save as draft</div>
              <div style={{ color: '#666', fontSize: 12 }}>Creates in Documenso without sending to customer</div>
            </div>
          </label>

          {/* Approve button */}
          <button
            onClick={handleApprove}
            disabled={sending}
            style={{ display: 'block', width: '100%', padding: '15px', borderRadius: 8, border: 'none', background: sending ? '#555' : '#e63329', color: '#fff', fontSize: 15, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', boxShadow: sending ? 'none' : '0 4px 16px rgba(230,51,41,0.4)' }}
          >
            {sending ? '⏳ Sending to Documenso...' : saveAsDraft ? '📋 Create Draft in Documenso' : '✓ Approve & Send to Documenso'}
          </button>

          {pdfOpened && !sending && (
            <p style={{ color: '#4ade80', fontSize: 12, textAlign: 'center', margin: '12px 0 0' }}>✓ PDF reviewed — ready to approve</p>
          )}
        </div>
      </div>
    </div>
  );
}
